import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { faCircleExclamation, faImage } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import DOMPurify from 'dompurify';
import InlineSVG from 'react-inlinesvg';

import { ImageGenerationSpinner } from '@/components/shared/image-generation-loader';
import { Icon } from '@/components/ui/icon';
import { SmallText } from '@/components/ui/typography';
import { isSvgImage } from '@/lib/assets-config';
import { getPendingRunId } from '@/lib/image-generation-helpers';
import { cn } from '@/lib/utils';
import { type PendingImageStatus, usePendingImagesStore } from '@/stores/pending-images-store';
import { getImageUrl, type ImageValue, isPendingAIImage } from '@/types/image';
import { useImagePriority } from './image-priority-context';

const preservedPaint = /^(?:(?:none|currentColor|inherit|transparent)$|url\()/i;
const paintAttributes = ['fill', 'stroke'] as const;

function sanitizeAndRecolorSvg(code: string): string {
  const sanitized = DOMPurify.sanitize(code, {
    USE_PROFILES: { svg: true, svgFilters: true },
    RETURN_DOM_FRAGMENT: true,
  });
  for (const element of sanitized.querySelectorAll('[fill], [stroke]')) {
    // Mask colors control visibility; recoloring white reduces luminance-mask opacity.
    if (element.closest('mask')) continue;

    for (const attribute of paintAttributes) {
      const paintValue = element.getAttribute(attribute);
      if (paintValue !== null && !preservedPaint.test(paintValue.trim())) {
        element.setAttribute(attribute, 'currentColor');
      }
    }
  }
  const container = sanitized.ownerDocument.createElement('div');
  container.append(sanitized);
  return container.innerHTML;
}

const statusLabels: Record<PendingImageStatus, string> = {
  pending: 'Starting image generation...',
  generating: 'Generating image...',
  completed: 'Image ready',
  failed: 'Image generation failed',
};

export function DefaultImageFallback({ className }: { className?: string }) {
  return (
    <div className={cn('flex h-full w-full items-center justify-center', className)}>
      <Icon icon={faImage} />
    </div>
  );
}

export function NoImagePlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon icon={faImage} className="size-3.5" />
        No image
      </span>
    </div>
  );
}

function ImageLoadingPlaceholder({
  runId,
  className,
  fallback,
}: {
  runId?: string;
  className?: string;
  fallback?: ReactNode;
}) {
  const pendingImage = usePendingImagesStore((state) =>
    runId ? state.getPendingImageByRunId(runId) : undefined,
  );
  const surfaceClass = 'h-full w-full bg-gradient-to-br from-foreground/80 to-foreground/90';

  if (!pendingImage) {
    return fallback || <div className={cn(surfaceClass, className)} />;
  }

  const isFailed = pendingImage.status === 'failed';
  const livePoliteness = isFailed ? 'assertive' : 'polite';

  return (
    <div className={cn(surfaceClass, 'flex items-center justify-center p-3', className)}>
      <div
        aria-live={livePoliteness}
        aria-atomic="true"
        className="flex flex-col items-center gap-2 text-center text-primary-foreground text-shadow-lg"
      >
        {isFailed ? (
          <Icon icon={faCircleExclamation} className="text-destructive" size="3x" />
        ) : (
          <ImageGenerationSpinner className="text-[48px] text-primary-foreground" />
        )}
        <SmallText className="leading-snug">
          {isFailed && pendingImage.error ? pendingImage.error : statusLabels[pendingImage.status]}
        </SmallText>
      </div>
    </div>
  );
}

interface ImageWithLoadingProps {
  image?: ImageValue | null;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  fallback?: ReactNode;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  inlineSvg?: boolean;
  'aria-hidden'?: boolean;
}

export function ImageWithLoading({
  image,
  alt = 'Image',
  className,
  style,
  fallback,
  loading: loadingProp,
  fetchPriority: fetchPriorityProp,
  inlineSvg = true,
  'aria-hidden': ariaHidden,
}: ImageWithLoadingProps) {
  const isPriority = useImagePriority();
  const loading = loadingProp ?? (isPriority ? 'eager' : 'lazy');
  const fetchPriority = fetchPriorityProp ?? (isPriority ? 'high' : undefined);
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);
  const imageElementRef = useRef<HTMLImageElement>(null);
  const runId = isPendingAIImage(image) ? (getPendingRunId(image) ?? undefined) : undefined;
  const pendingImage = usePendingImagesStore((state) =>
    runId ? state.getPendingImageByRunId(runId) : undefined,
  );
  const isGenerating = pendingImage?.status === 'pending' || pendingImage?.status === 'generating';
  const imageSrc = getImageUrl(image, 'large');
  const imageFailedToLoad = imageSrc === failedImageSrc;
  const hasFallback = fallback !== undefined && fallback !== null;
  const shouldShowFallback = imageFailedToLoad && hasFallback;
  const loadingStatusLabel = pendingImage
    ? statusLabels[pendingImage.status]
    : statusLabels.generating;

  useEffect(() => {
    const imageElement = imageElementRef.current;
    if (imageSrc && imageElement?.complete && imageElement.naturalWidth === 0) {
      setFailedImageSrc(imageSrc);
    }
  }, [imageSrc]);

  if (isGenerating && imageSrc && !shouldShowFallback) {
    return (
      <div className="relative h-full w-full overflow-hidden">
        <img
          ref={imageElementRef}
          src={imageSrc}
          alt={alt}
          aria-hidden={ariaHidden}
          className={cn('opacity-30', className)}
          style={style}
          loading={loading}
          fetchPriority={fetchPriority}
          onError={() => setFailedImageSrc(imageSrc)}
        />
        <div className="pointer-events-none absolute inset-0 grid place-items-center bg-gradient-to-br from-foreground/30 via-foreground/70 to-foreground/80 p-3">
          <div
            aria-live="polite"
            aria-atomic="true"
            className="flex flex-col items-center gap-2 text-center text-primary-foreground text-shadow-lg"
          >
            <ImageGenerationSpinner className="text-[48px] text-primary-foreground" />
            <SmallText className="leading-snug">{loadingStatusLabel}</SmallText>
          </div>
        </div>
      </div>
    );
  }

  if (isGenerating) {
    return <ImageLoadingPlaceholder runId={runId} className={className} fallback={fallback} />;
  }

  if (imageSrc && inlineSvg && isSvgImage(image)) {
    return (
      <InlineSVG
        src={imageSrc}
        title={alt}
        className={cn(
          'flex h-full w-full items-center justify-center text-primary [&>svg]:max-h-full [&>svg]:max-w-full',
          className,
        )}
        style={style}
        aria-hidden={ariaHidden}
        cacheRequests
        preProcessor={sanitizeAndRecolorSvg}
      >
        <img
          src={imageSrc}
          alt={alt}
          aria-hidden={ariaHidden}
          className={className}
          style={style}
          loading={loading}
          fetchPriority={fetchPriority}
        />
      </InlineSVG>
    );
  }

  if (imageSrc && !shouldShowFallback) {
    return (
      <img
        ref={imageElementRef}
        src={imageSrc}
        alt={alt}
        aria-hidden={ariaHidden}
        className={className}
        style={style}
        loading={loading}
        fetchPriority={fetchPriority}
        onError={() => setFailedImageSrc(imageSrc)}
      />
    );
  }

  return fallback ?? null;
}
